/* eslint-disable class-methods-use-this */
import axios from 'axios';
import rateLimit from 'axios-rate-limit';
import store from '../../store';
import variables from '../_variables';

const { apiPath, cdnPath, trackPath } = variables;

export default class Axios {
	constructor(nprogress) {
		this.isRefreshing = false;
		this.failedQueue = [];
		this.axios = rateLimit(axios.create(), { maxRequests: 10, perMilliseconds: 500 });
		this.token = rateLimit(axios.create(), { maxRequests: 1, perMilliseconds: 1000 });

		this.nprogress = nprogress;

		this.request();
		this.response();
	}

	request() {
		const self = this;

		this.axios.interceptors.request.use((config) => {
			this.nprogress.start();

			const baseType = config.headers.baseType ? config.headers.baseType : null;
			config.baseURL = apiPath;
			if (baseType === 'cdn') config.baseURL = cdnPath;
			if (baseType === 'tracking') config.baseURL = trackPath;
			delete config.headers.baseType;

			if (!config.headers.byPassAuth) {
				const jwtToken = localStorage.getItem('jwtToken');
				let refreshToken = localStorage.getItem('refreshToken');
				const validToken = self._validJWTToken(jwtToken, refreshToken);

				if (validToken) {
					config.headers.Authorization = `Bearer ${jwtToken}`;
				} else {
					if (!config._retry && refreshToken) {
						if (self.isRefreshing) {
							return new Promise(((resolve, reject) => {
								self.failedQueue.push({
									resolve,
									reject,
								});
							})).then((token) => {
								config.headers.Authorization = `Bearer ${token}`;
								return config;
							}).catch(err => Promise.reject(err));
						}

						config._retry = true;
						self.isRefreshing = true;

						let tries = 0;
						const refreshFn = (resolve, reject) => {
							refreshToken = JSON.parse(localStorage.getItem('refreshToken'));
							tries += 1;

							this.token.post(`${apiPath}auth/refreshToken`, { token: refreshToken.token }).then(({ data }) => {
								store.commit('LOGIN', {
									jwt_token: data.jwt_token,
									refresh_token: data.refresh_token,
									user: data.user,
								});

								config.headers.Authorization = `Bearer ${data.jwt_token}`;
								self._processQueue(null, data.jwt_token);
								resolve(config);
							}).catch((err) => {
								if (tries <= 7) {
									setTimeout(() => refreshFn(resolve, reject), tries * 1000);
								} else {
									store.dispatch('logout');
									self._processQueue(err, null);
									reject(err);
								}
							}).then(() => {
								setTimeout(() => { self.isRefreshing = false; }, 10000);
							});
						};

						return new Promise(refreshFn);
					}

					if (config.headers.Authorization) {
						// eslint-disable-next-line prefer-promise-reject-errors
						return Promise.reject({
							ok: false,
							reason: 'AUTH_REQUIRED',
						});
					}
				}
			}

			delete config.headers.byPassAuth;
			return config;
		});
	}

	response() {
		this.axios.interceptors.response.use((response) => {
			const self = this;
			const { requestPreventDestructure } = store.getters;

			if (!requestPreventDestructure) {
				Object.keys(response.data).forEach((key) => {
					const obj = response.data[key];

					if (Array.isArray(obj)) {
						const data = [];

						obj.forEach((item) => {
							data.push(self._destructureObject(item));
						});

						response.data[key] = data;
					} else if (obj instanceof Object) {
						response.data[key] = self._destructureObject(obj);
					}
				});
			}

			this.nprogress.done();
			return response;
		}, (error) => {
			const { requestSilent } = store.getters;
			this.nprogress.done();

			if (requestSilent) return Promise.reject(error);
			return store.dispatch('renderError', error);
		});
	}

	_destructureObject(obj) {
		const keys = Object.keys(obj);
		if (!keys.some(k => k.includes('.'))) return obj;

		const ret = {};
		keys.forEach((key) => {
			const parts = key.split('.');
			const val = obj[key];

			if (parts[1]) {
				if (!ret[parts[0]]) ret[parts[0]] = {};
				ret[parts[0]][parts[1]] = val;
			} else {
				ret[parts[0]] = val;
			}
		});

		return ret;
	}

	_processQueue = (error, token = null) => {
		this.failedQueue.forEach((prom) => {
			if (error) {
				prom.reject(error);
			} else {
				prom.resolve(token);
			}
		});

		this.failedQueue = [];
	}

	_validJWTToken(jwtToken, refreshToken) {
		if (!jwtToken) jwtToken = localStorage.getItem('jwtToken');
		if (!refreshToken) refreshToken = localStorage.getItem('refreshToken');

		if (jwtToken && refreshToken) {
			const jwtData = jwtToken.split('.')[1];
			const data = JSON.parse(
				window.atob(jwtData.replace(/-/g, '+').replace(/_/g, '/')),
			);


			const jwtExpires = new Date(data.exp * 1000).getTime();
			const now = parseInt(new Date().getTime(), 10); // + 7170000

			if (jwtExpires <= now) {
				return false;
			}

			return true;
		}

		return false;
	}
}
